import React from 'react';
import { Image, Platform, type ImageSourcePropType, type ImageStyle, type StyleProp } from 'react-native';

type Props = {
  source: ImageSourcePropType;
  /** Цвет монохромной иконки; меняется вместе с темой. */
  tintColor: string;
  style?: StyleProp<ImageStyle>;
  resizeMode?: 'contain' | 'cover' | 'stretch' | 'center' | 'repeat';
};

/**
 * Картинка-иконка, перекрашиваемая темой.
 *
 * <p><b>Зачем обёртка вместо голого {@code <Image tintColor>}.</b> В вебе react-native-web
 * красит иконку не свойством, а SVG-фильтром: рядом с картинкой рендерится
 * {@code <filter id="tint-N"><feFlood flood-color="…">}, а сама картинка — это {@code <div>} с
 * {@code filter: url(#tint-N)}. Идентификатор фильтра выдаётся один раз на экземпляр
 * ({@code useRef(_filterId++)}) и при смене цвета не меняется. Браузеры не перерисовывают
 * элемент, ссылающийся на фильтр через CSS, когда содержимое самого фильтра поменялось, —
 * поэтому иконка оставалась цвета прошлой темы до перезагрузки страницы, хотя весь остальной
 * экран уже перекрасился.
 *
 * <p><b>Что делает {@code key}.</b> Ключ по цвету заставляет React пересоздать {@code Image}:
 * новый экземпляр берёт новый {@code tint-N+1}, и {@code div} начинает ссылаться на другой
 * фильтр — то есть на тот, который браузер ещё не рисовал. Мигания нет: картинка уже в кэше
 * загрузчика, и новый экземпляр стартует сразу в состоянии {@code LOADED}.
 *
 * <p>На нативе {@code tintColor} обновляется сам, и лишний перемонтаж там не нужен — ключ
 * ставится только в вебе.
 */
export function TintedIcon({ source, tintColor, style, resizeMode = 'contain' }: Props) {
  return (
    <Image
      key={Platform.OS === 'web' ? tintColor : undefined}
      source={source}
      style={style}
      tintColor={tintColor}
      resizeMode={resizeMode}
    />
  );
}
